import { useCallback, useState } from 'react';
import { useEditorStore } from '../../../stores/editor.js';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

interface RevertResponseBody {
  data: {
    status: 'reverted';
    imageUrl: string;
    canRevert: false;
  };
}

/**
 * POST /api/v1/edit/{sessionId}/revert — one-step undo of the most recent
 * submit. On success the canvas swaps to the previous image bytes (the
 * server-side snapshot) and the Revert button hides until the next submit
 * creates a fresh snapshot.
 *
 * Mask + description are intentionally untouched (non-destructive revert).
 * The user can resubmit the same mask on the previous base, or undo their
 * strokes with Ctrl+Z if they want a clean slate.
 */
export function useRevertEdit(sessionId: string | undefined) {
  const [isReverting, setIsReverting] = useState(false);
  const setCurrentImageUrl = useEditorStore((s) => s.setCurrentImageUrl);
  const setResultImageUrl = useEditorStore((s) => s.setResultImageUrl);
  const setSubmissionStatus = useEditorStore((s) => s.setSubmissionStatus);
  const setSubmissionError = useEditorStore((s) => s.setSubmissionError);
  const setCanRevert = useEditorStore((s) => s.setCanRevert);

  const revert = useCallback(async (): Promise<boolean> => {
    if (!sessionId || isReverting) return false;
    setIsReverting(true);
    setSubmissionError(null);

    try {
      const res = await fetch(`${API_BASE}/v1/edit/${sessionId}/revert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (res.ok) {
        const body = (await res.json()) as RevertResponseBody;
        // Swap the canvas base back to the previous bytes. EditorCanvas's
        // imageUrl effect re-inits and (if dims match, which they will for
        // a one-step revert) restores the preserved mask.
        setCurrentImageUrl(body.data.imageUrl);
        // No pending result anymore — the review overlay was already
        // dismissed by the user's draw (or it was still up), either way
        // we drop back to plain editing.
        setResultImageUrl(null);
        setSubmissionStatus('idle');
        setCanRevert(false);
        return true;
      }

      const errBody = (await res.json().catch(() => ({}))) as {
        error?: { code?: string; message?: string };
      };
      const message = errBody.error?.message ?? 'Could not revert. Please try again.';
      setSubmissionError(message);
      // 409 INVALID_STATE specifically means the snapshot is gone — hide
      // the button so the user can't keep retrying a futile call.
      if (errBody.error?.code === 'EDIT_SESSION_INVALID_STATE') {
        setCanRevert(false);
      }
      return false;
    } catch {
      setSubmissionError('Network error while reverting. Please try again.');
      return false;
    } finally {
      setIsReverting(false);
    }
  }, [
    sessionId,
    isReverting,
    setCurrentImageUrl,
    setResultImageUrl,
    setSubmissionStatus,
    setSubmissionError,
    setCanRevert,
  ]);

  return { revert, isReverting };
}
